import Mention from "@tiptap/extension-mention"
import { nodePasteRule } from "@tiptap/core"
import { buildEditorContent } from "~/common/promptDocument"
import { ReactNodeViewRenderer } from "@tiptap/react"
import type { SuggestionProps } from "@tiptap/suggestion"
import { createElement, type RefObject } from "react"
import {
  getMentionResources,
  type MentionResource,
} from "~/common/media/resourceMentions"
import type { UploadedResource } from "~/common/media/uploadedResources"
import styles from "./index.css"
import { MentionToken } from "./mentionRenderers"
import {
  INITIAL_SUGGESTION_STATE,
  getSuggestionPosition,
  insertMentionResource,
  toMentionAttrs,
} from "./mentionUtils"
import type {
  MentionNodeAttrs,
  MentionTokenClickHandler,
  SuggestionState,
  SuggestionStateUpdater,
} from "./types"

type ResourceMentionExtensionOptions = {
  containerRef: RefObject<HTMLDivElement | null>
  mentionableResourcesRef: RefObject<UploadedResource[]>
  onMentionClick: MentionTokenClickHandler
  suggestionStateRef: RefObject<SuggestionState | null>
  updateSuggestionState: (updater: SuggestionStateUpdater) => void
}

export const createResourceMentionExtension = ({
  containerRef,
  mentionableResourcesRef,
  onMentionClick,
  suggestionStateRef,
  updateSuggestionState,
}: ResourceMentionExtensionOptions) => {
  return Mention.extend({
    name: "resourceMention",
    addPasteRules() {
      return [
        nodePasteRule({
          find: /@(图片|视频|音频)\d+/g,
          type: this.type,
          getAttributes: (match) =>
            buildEditorContent({
              mentionableResources: mentionableResourcesRef.current,
              value: match[0],
            }).content?.[0].content?.[0].attrs || null,
        }),
      ]
    },
    addAttributes() {
      const parentAttributes = this.parent?.() ?? {}

      // Tiptap 只会保留 schema 中声明过的 attrs；资源类型不声明会在插入后丢失，导致图片节点按音频兜底渲染。
      return {
        ...parentAttributes,
        kind: {
          default: null,
          parseHTML: (element) => {
            return element.getAttribute("data-kind")
          },
          renderHTML: (attributes) => {
            if (!attributes.kind) return {}

            return {
              "data-kind": attributes.kind,
            }
          },
        },
        missing: {
          default: false,
          parseHTML: (element) => {
            return element.getAttribute("data-missing") === "true"
          },
          renderHTML: (attributes) => {
            if (!attributes.missing) return {}

            return {
              "data-missing": "true",
            }
          },
        },
        name: {
          default: null,
          parseHTML: (element) => {
            return element.getAttribute("data-name")
          },
          renderHTML: (attributes) => {
            if (!attributes.name) return {}

            return {
              "data-name": attributes.name,
            }
          },
        },
        type: {
          default: null,
          parseHTML: (element) => {
            return element.getAttribute("data-resource-type")
          },
          renderHTML: (attributes) => {
            if (!attributes.type) return {}

            return {
              "data-resource-type": attributes.type,
            }
          },
        },
        url: {
          default: null,
          parseHTML: (element) => {
            return element.getAttribute("data-url")
          },
          renderHTML: (attributes) => {
            if (!attributes.url) return {}

            return {
              "data-url": attributes.url,
            }
          },
        },
      }
    },
    addNodeView() {
      return ReactNodeViewRenderer((props) => {
        return createElement(MentionToken, {
          ...props,
          onMentionClick,
        })
      })
    },
  }).configure({
    HTMLAttributes: {
      class: styles.mentionMarkup,
    },
    renderText: ({ node }) => {
      return `@${node.attrs.label}`
    },
    renderHTML: ({ node }) => {
      return [
        "span",
        {
          "data-type": "resourceMention",
          "data-id": node.attrs.id,
          "data-label": node.attrs.label,
          "data-kind": node.attrs.kind,
          "data-name": node.attrs.name,
          "data-missing": node.attrs.missing ? "true" : undefined,
          "data-resource-type": node.attrs.type,
          "data-url": node.attrs.url,
        },
        `@${node.attrs.label}`,
      ]
    },
    suggestion: {
      allowedPrefixes: null,
      char: "@",
      items: (item) => {
        const normalizedQuery =
          typeof item.query === "string" ? item.query.toLowerCase() : ""

        return getMentionResources(mentionableResourcesRef.current).filter(
          (resource) => {
            const keyword = [resource.label, resource.name || ""]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
            return keyword.includes(normalizedQuery)
          },
        )
      },
      render: () => {
        const updateMenu = (
          props: SuggestionProps<MentionResource, MentionNodeAttrs>,
          resetIndex: boolean,
        ) => {
          const rect = props.clientRect?.()
          const container = containerRef.current

          if (!rect || !container) return

          updateSuggestionState((currentValue) => ({
            activeIndex: resetIndex
              ? 0
              : Math.min(
                  currentValue.activeIndex,
                  Math.max(props.items.length - 1, 0),
                ),
            command: (resource) => {
              props.command(toMentionAttrs(resource))
            },
            items: props.items,
            open: true,
            position: getSuggestionPosition(container, rect),
          }))
        }

        return {
          onStart: (props) => updateMenu(props, true),
          onUpdate: (props) => updateMenu(props, false),
          onKeyDown: ({ event }) => {
            switch (event.key) {
              case "ArrowDown":
              case "ArrowUp": {
                const direction = event.key === "ArrowDown" ? 1 : -1
                updateSuggestionState((currentValue) => {
                  if (!currentValue.items.length) return currentValue

                  return {
                    ...currentValue,
                    activeIndex:
                      (currentValue.activeIndex +
                        direction +
                        currentValue.items.length) %
                      currentValue.items.length,
                  }
                })
                return true
              }
              case "Enter": {
                const currentValue = suggestionStateRef.current
                const selectedItem =
                  currentValue?.items[currentValue.activeIndex]
                if (selectedItem) {
                  currentValue.command?.(selectedItem)
                  updateSuggestionState((previousValue) => ({
                    ...previousValue,
                    open: false,
                  }))
                  return true
                }
                break
              }
              case "Escape":
                updateSuggestionState((currentValue) => ({
                  ...currentValue,
                  open: false,
                }))
                return true
            }
            return false
          },
          onExit: () => {
            updateSuggestionState(INITIAL_SUGGESTION_STATE)
          },
        }
      },
      command: ({ editor: currentEditor, range, props }) => {
        const mentionAttrs = props as MentionNodeAttrs

        insertMentionResource(currentEditor, mentionAttrs, range)
      },
    },
  })
}
